import React, { useState, useEffect } from "react";
import { apiClient } from "../apiClient";

export default function DeliveryDashboard({ 
  plans = [],
  loading, 
  onFilterChange 
}) {
  const userRole = localStorage.getItem("userRole");
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [showDriverStats, setShowDriverStats] = useState(true);
  
  // Filter state
  const [filters, setFilters] = useState({
    dateFrom: new Date().toISOString().split("T")[0],
    dateTo: new Date().toISOString().split("T")[0],
    driverId: ""
  });

  // ==========================================
  // FETCH DRIVERS
  // ==========================================
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const data = await apiClient.get("/auth/users");
        setDrivers(data.filter(user => user.role === "driver"));
      } catch (err) {
        setError("Failed to load drivers. Please refresh the page.");
      }
    };

    if (userRole === "admin" || userRole === "manager") {
      fetchDrivers();
    }
  }, [userRole]);

  // ==========================================
  // FETCH CLIENTS
  // ==========================================
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const data = await apiClient.get("/clients/");
        setClients(data);
      } catch (err) {
        setError("Failed to load clients. Please refresh the page.");
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
  }, []);

  // ==========================================
  // HELPER: GET CLIENT NAME BY ID
  // ==========================================
  const getClientName = (clientId) => {
    if (clientsLoading) return "Loading...";
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : `Client #${clientId}`;
  };

  // ==========================================
  // CALCULATE SUMMARY STATISTICS
  // ==========================================
  const calculateSummary = () => {
    if (!plans || plans.length === 0) {
      return {
        totalDeliveries: 0,
        totalDrivers: 0,
        totalItems: 0,
        totalValue: 0,
        paidAmount: 0,
        partialAmount: 0,
        dueAmount: 0
      };
    }

    const uniqueDrivers = new Set();
    let totalItems = 0;
    let totalValue = 0;
    let paidAmount = 0;
    let partialAmount = 0;
    let dueAmount = 0;

    plans.forEach(plan => {
      // Count unique drivers
      if (plan.driver_name || plan.assigned_user_id) {
        uniqueDrivers.add(plan.driver_name || plan.assigned_user_id);
      }

      // Process items
      if (plan.items && plan.items.length > 0) {
        plan.items.forEach(item => {
          totalItems += item.quantity || 0;
          const itemValue = item.total_value_groszy || 0;
          totalValue += itemValue;

          // Calculate payment amounts by status
          const paidSoFar = item.payment_amount_groszy || 0;
          
          if (item.payment_status === "Paid") {
            paidAmount += itemValue;
          } else if (item.payment_status === "Partial Paid") {
            partialAmount += paidSoFar;
            dueAmount += (itemValue - paidSoFar);
          } else {
            dueAmount += itemValue;
          }
        });
      }
    });

    return {
      totalDeliveries: plans.length,
      totalDrivers: uniqueDrivers.size,
      totalItems,
      totalValue,
      paidAmount,
      partialAmount,
      dueAmount
    };
  };

  // ==========================================
  // FILTER HANDLERS
  // ==========================================
  const handleApplyFilters = () => {
    if (onFilterChange) {
      onFilterChange(filters);
    }
  };

  const handleClearFilters = () => {
    const today = new Date().toISOString().split("T")[0];
    const clearedFilters = {
      dateFrom: today,
      dateTo: today,
      driverId: ""
    };
    setFilters(clearedFilters);
    if (onFilterChange) {
      onFilterChange(clearedFilters);
    }
  };

  // ==========================================
  // GROUP PLANS BY DATE AND DRIVER
  // ==========================================
  const groupedPlans = (plans || []).reduce((acc, plan) => {
    const date = plan.date || "Unknown";
    const driverName = plan.driver_name || `Driver ${plan.assigned_user_id}`;
    
    if (!acc[date]) acc[date] = {};
    if (!acc[date][driverName]) acc[date][driverName] = [];
    
    acc[date][driverName].push(plan);
    return acc;
  }, {});

  // ==========================================
  // PAYMENT STATUS COLOR
  // ==========================================
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800 border-green-300";
      case "Partial Paid":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "Not Paid":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  // ==========================================
  // RENDER
  // ==========================================
  if (loading) {
    return (
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
        <p className="text-[#2D2F5E] italic">Loading delivery plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-xl shadow-sm border border-red-300">
        <p className="text-red-800 font-medium">⚠️ {error}</p>
      </div>
    );
  }

  const summary = calculateSummary();

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
      <h3 className="font-bold text-[#2D2F5E] text-lg mb-4">📋 Delivery Plans</h3>

      {/* ==========================================
          FILTERS SECTION
      ========================================== */}
      {(userRole === "admin" || userRole === "manager") && (
        <div className="mb-4 p-4 bg-[#F5E9D8] rounded-lg border border-[#2D2F5E] border-opacity-20">
          <h4 className="font-medium text-[#2D2F5E] mb-3 text-sm">🔍 Filters</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-[#2D2F5E] mb-1">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[#2D2F5E] mb-1">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[#2D2F5E] mb-1">Driver</label>
              <select
                value={filters.driverId}
                onChange={(e) => setFilters({ ...filters, driverId: e.target.value ? Number(e.target.value) : "" })}
                className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-sm text-[#2D2F5E] bg-white"
              >
                <option value="">All Drivers</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.username}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleApplyFilters}
              className="flex-1 bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded-lg transition text-sm font-medium"
            >
              Apply Filters
            </button>
            <button
              onClick={handleClearFilters}
              className="bg-[#2D2F5E] hover:bg-[#1D1F4E] text-white px-4 py-2 rounded-lg transition text-sm font-medium"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ==========================================
          DRIVER-WISE STATISTICS (NEW)
      ========================================== */}
      {plans && plans.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setShowDriverStats(!showDriverStats)}
            className="w-full bg-gradient-to-r from-[#2D2F5E] to-[#3D3F6E] text-white px-4 py-3 rounded-lg font-bold flex items-center justify-between hover:opacity-90 transition"
          >
            <span>👥 Driver-Wise Statistics</span>
            <span>{showDriverStats ? "▼" : "▶"}</span>
          </button>

          {showDriverStats && (
            <div className="mt-3 bg-white border border-[#2D2F5E] border-opacity-20 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5E9D8]">
                    <tr>
                      <th className="text-left p-3 font-bold text-[#2D2F5E]">Driver</th>
                      <th className="text-right p-3 font-bold text-[#2D2F5E]">Deliveries</th>
                      <th className="text-right p-3 font-bold text-[#2D2F5E]">Total Amount</th>
                      <th className="text-right p-3 font-bold text-[#2D2F5E]">Paid</th>
                      <th className="text-right p-3 font-bold text-[#2D2F5E]">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const driverStats = {};
                      
                      plans.forEach(plan => {
                        const driverName = plan.driver_name || `Driver ${plan.assigned_user_id}`;
                        
                        if (!driverStats[driverName]) {
                          driverStats[driverName] = {
                            deliveries: 0,
                            totalAmount: 0,
                            paidAmount: 0,
                            dueAmount: 0
                          };
                        }
                        
                        driverStats[driverName].deliveries += 1;
                        
                        if (plan.items && plan.items.length > 0) {
                          plan.items.forEach(item => {
                            const itemValue = item.total_value_groszy || 0;
                            driverStats[driverName].totalAmount += itemValue;
                            
                            const paidSoFar = item.payment_amount_groszy || 0;
                            
                            if (item.payment_status === "Paid") {
                              driverStats[driverName].paidAmount += itemValue;
                            } else if (item.payment_status === "Partial Paid") {
                              driverStats[driverName].paidAmount += paidSoFar;
                              driverStats[driverName].dueAmount += (itemValue - paidSoFar);
                            } else {
                              driverStats[driverName].dueAmount += itemValue;
                            }
                          });
                        }
                      });
                      
                      return Object.entries(driverStats).map(([driverName, stats]) => (
                        <tr key={driverName} className="border-t border-[#2D2F5E] border-opacity-10 hover:bg-[#F5E9D8]">
                          <td className="p-3 font-medium text-[#2D2F5E]">🚚 {driverName}</td>
                          <td className="p-3 text-right text-[#2D2F5E]">{stats.deliveries}</td>
                          <td className="p-3 text-right font-bold text-[#2D2F5E]">
                            {(stats.totalAmount / 100).toFixed(2)} PLN
                          </td>
                          <td className="p-3 text-right font-bold text-green-700">
                            {(stats.paidAmount / 100).toFixed(2)} PLN
                          </td>
                          <td className="p-3 text-right font-bold text-red-700">
                            {(stats.dueAmount / 100).toFixed(2)} PLN
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==========================================
          SUMMARY STATISTICS SECTION
      ========================================== */}
      {plans && plans.length > 0 && (
        <div className="mb-4 space-y-3">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Total Deliveries */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="text-xs text-blue-700 font-medium mb-1">📦 Deliveries</div>
              <div className="text-2xl font-bold text-blue-800">{summary.totalDeliveries}</div>
            </div>

            {/* Active Drivers */}
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div className="text-xs text-purple-700 font-medium mb-1">🚚 Drivers</div>
              <div className="text-2xl font-bold text-purple-800">{summary.totalDrivers}</div>
            </div>

            {/* Total Items */}
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
              <div className="text-xs text-orange-700 font-medium mb-1">🥙 Items</div>
              <div className="text-2xl font-bold text-orange-800">{summary.totalItems}</div>
            </div>

            {/* Total Value */}
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="text-xs text-green-700 font-medium mb-1">💵 Total Value</div>
              <div className="text-2xl font-bold text-green-800">
                {(summary.totalValue / 100).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Payment Status Summary */}
          <div className="bg-[#F5E9D8] p-4 rounded-lg border border-[#2D2F5E] border-opacity-20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-[#2D2F5E] font-medium">💰 Paid:</span>
                <span className="font-bold text-green-700">
                  {(summary.paidAmount / 100).toFixed(2)} PLN
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#2D2F5E] font-medium">🟡 Partial:</span>
                <span className="font-bold text-yellow-700">
                  {(summary.partialAmount / 100).toFixed(2)} PLN
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#2D2F5E] font-medium">❌ Due:</span>
                <span className="font-bold text-red-700">
                  {(summary.dueAmount / 100).toFixed(2)} PLN
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          DELIVERY PLANS LIST
      ========================================== */}
      {!plans || plans.length === 0 ? (
        <p className="text-[#2D2F5E] text-sm italic">No delivery plans found for selected filters.</p>
      ) : (
        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
          {Object.keys(groupedPlans).sort().reverse().map((date) => (
            <div key={date} className="border border-[#2D2F5E] border-opacity-20 rounded-lg overflow-hidden">
              {/* Date Header */}
              <div className="bg-gradient-to-r from-[#2D2F5E] to-[#3D3F6E] text-white px-4 py-2 font-bold">
                📅 {date}
              </div>

              {/* Drivers for this date */}
              <div className="p-3 space-y-3">
                {Object.keys(groupedPlans[date]).map((driverName) => (
                  <div key={driverName} className="bg-[#F5E9D8] rounded-lg p-3 border border-[#2D2F5E] border-opacity-20">
                    <div className="font-bold text-[#2D2F5E] mb-2 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center">
                        <span className="mr-2">🚚</span>
                        {driverName}
                      </div>
                      {/* Display Vehicle Number */}
                      {groupedPlans[date][driverName][0]?.vehicle_number && (
                        <span className="text-sm bg-white px-3 py-1 rounded border border-[#2D2F5E] border-opacity-20 font-medium">
                          🚐 {groupedPlans[date][driverName][0].vehicle_number}
                        </span>
                      )}
                    </div>

                    {/* Client deliveries for this driver */}
                    <div className="space-y-2">
                      {groupedPlans[date][driverName].map((plan) => {
                        const totalValue = plan.items?.reduce((sum, item) => sum + (item.total_value_groszy || 0), 0) || 0;
                        const allStatuses = plan.items?.map(item => item.payment_status) || [];
                        const overallStatus = allStatuses.every(s => s === "Paid") 
                          ? "Paid" 
                          : allStatuses.some(s => s === "Paid" || s === "Partial Paid")
                          ? "Partial Paid"
                          : "Not Paid";

                        // Get client name
                        const clientId = plan.items?.[0]?.client_id;
                        const clientName = clientId ? getClientName(clientId) : "Unknown Client";

                        return (
                          <div
                            key={plan.id}
                            className="bg-white p-3 rounded border border-[#2D2F5E] border-opacity-10"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium text-[#2D2F5E]">
                                📦 Client: {clientName}
                              </div>
                              <span className={`text-xs px-2 py-1 rounded border font-medium ${getPaymentStatusColor(overallStatus)}`}>
                                {overallStatus}
                              </span>
                            </div>

                            {plan.items && plan.items.length > 0 ? (
                              <div>
                                <ul className="space-y-1 text-sm">
                                  {plan.items.map((item, i) => (
                                    <li key={i} className="text-[#2D2F5E] flex justify-between">
                                      <span>• {item.quantity} × {item.item_name}</span>
                                      <span className="font-medium">{(item.total_value_groszy / 100).toFixed(2)} PLN</span>
                                    </li>
                                  ))}
                                </ul>
                                
                                {/* Display "Others" items if present */}
                                {plan.others && (
                                  <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                                    <div className="text-xs font-medium text-yellow-800 mb-1">🛒 Other Items:</div>
                                    <div className="text-sm text-yellow-900 whitespace-pre-wrap">{plan.others}</div>
                                  </div>
                                )}

                                <div className="mt-2 pt-2 border-t border-[#2D2F5E] border-opacity-20 text-sm font-bold text-[#2D2F5E] flex justify-between">
                                  <span>Total:</span>
                                  <span>{(totalValue / 100).toFixed(2)} PLN</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-[#2D2F5E] italic">No items</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}